import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

// Initialize default tasks
const defaultDailyTasks = {
  'd1': { time: '9:00 AM', task: 'AM Platform Sweep', icon: 'Sunrise', completed: false },
  'd2': { time: '10:00 AM', task: 'Approve Scheduled Content', icon: 'CheckSquare', completed: false },
  'd3': { time: '11:00 AM', task: 'Monitor Trends', icon: 'TrendingUp', completed: false },
  'd4': { time: '4:00 PM', task: 'PM Sweep + Replies', icon: 'MessageCircle', completed: false },
  'd5': { time: '5:00 PM', task: 'Daily KPI Snapshot', icon: 'BarChart2', completed: false }
};

const defaultWeeklyTasks = {
  'Mon': {
    title: 'Research',
    tasks: [
      { id: 'm1', text: 'Trend Analysis', completed: false },
      { id: 'm2', text: 'Competitor Audit', completed: false },
      { id: 'm3', text: 'Scripting', completed: false }
    ]
  },
  'Tue': {
    title: 'Drafting',
    tasks: [
      { id: 't1', text: 'Write Captions', completed: false },
      { id: 't2', text: 'Creative Briefs', completed: false },
      { id: 't3', text: 'Carousel Copy', completed: false }
    ]
  },
  'Wed': {
    title: 'Assets',
    tasks: [
      { id: 'w1', text: 'Review Visuals', completed: false },
      { id: 'w2', text: 'Revisions', completed: false },
      { id: 'w3', text: 'Final Polish', completed: false }
    ]
  },
  'Thu': {
    title: 'Schedule',
    tasks: [
      { id: 'th1', text: 'Upload', completed: false },
      { id: 'th2', text: 'Scheduling', completed: false },
      { id: 'th3', text: 'Community Prep', completed: false }
    ]
  },
  'Fri': {
    title: 'Analytics',
    tasks: [
      { id: 'f1', text: 'Weekly Report', completed: false },
      { id: 'f2', text: 'Strategy Plan', completed: false },
      { id: 'f3', text: 'Top Performers', completed: false }
    ]
  }
};

// Load state from localStorage or use defaults
const loadTasksFromStorage = (key, defaultTasks) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultTasks;
  } catch (error) {
    console.warn('Error loading tasks from storage:', error);
    return defaultTasks;
  }
};

// Save state to localStorage
const saveTasksToStorage = (key, tasks) => {
  try {
    localStorage.setItem(key, JSON.stringify(tasks));
  } catch (error) {
    console.warn('Error saving tasks to storage:', error);
  }
};

const WorkflowEngine = () => {
  // Initialize state with persisted data or defaults
  const [dailyTasks, setDailyTasks] = useState(
    loadTasksFromStorage('workflow_dailyTasks', defaultDailyTasks)
  );
  
  const [weeklyTasks, setWeeklyTasks] = useState(
    loadTasksFromStorage('workflow_weeklyTasks', defaultWeeklyTasks)
  );

  const [dailyProgress, setDailyProgress] = useState(0);
  const [weeklyProgress, setWeeklyProgress] = useState(0);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    saveTasksToStorage('workflow_dailyTasks', dailyTasks);
  }, [dailyTasks]);

  useEffect(() => {
    saveTasksToStorage('workflow_weeklyTasks', weeklyTasks);
  }, [weeklyTasks]);

  // Calculate progress
  useEffect(() => {
    const completedDaily = Object.values(dailyTasks).filter(task => task.completed).length;
    setDailyProgress((completedDaily / Object.values(dailyTasks).length) * 100);
  }, [dailyTasks]);

  useEffect(() => {
    const allWeeklyTasks = Object.values(weeklyTasks).flatMap(day => day.tasks);
    const completedWeekly = allWeeklyTasks.filter(task => task.completed).length;
    setWeeklyProgress((completedWeekly / allWeeklyTasks.length) * 100);
  }, [weeklyTasks]);

  const toggleDailyTask = (taskId) => {
    setDailyTasks(prev => ({
      ...prev,
      [taskId]: { ...prev[taskId], completed: !prev[taskId].completed }
    }));
  };

  const toggleWeeklyTask = (day, taskId) => {
    setWeeklyTasks(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        tasks: prev[day].tasks.map(task =>
          task.id === taskId ? { ...task, completed: !task.completed } : task
        )
      }
    }));
  };

  const resetDailyTasks = () => {
    const resetTasks = Object.keys(dailyTasks).reduce((acc, key) => {
      acc[key] = { ...dailyTasks[key], completed: false };
      return acc;
    }, {});
    setDailyTasks(resetTasks);
  };

  const resetWeeklyTasks = () => {
    const resetTasks = Object.keys(weeklyTasks).reduce((acc, key) => {
      acc[key] = {
        ...weeklyTasks[key],
        tasks: weeklyTasks[key].tasks.map(task => ({ ...task, completed: false }))
      };
      return acc;
    }, {});
    setWeeklyTasks(resetTasks);
  };

  // Check if it's a new day/week for auto-reset (optional)
  useEffect(() => {
    const checkForAutoReset = () => {
      const now = new Date();
      const today = now.toDateString();
      const currentDay = now.toLocaleDateString('en-US', { weekday: 'short' });
      
      // Check if it's Monday and we should reset weekly tasks
      const lastResetDay = localStorage.getItem('workflow_lastWeeklyReset');
      if (currentDay === 'Mon' && lastResetDay !== today) {
        // Optional: Auto-reset weekly tasks on Monday
        // resetWeeklyTasks();
        localStorage.setItem('workflow_lastWeeklyReset', today);
      }
      
      // Check if it's a new day for daily tasks
      const lastResetDate = localStorage.getItem('workflow_lastDailyReset');
      if (lastResetDate !== today) {
        // Optional: Auto-reset daily tasks on new day
        // resetDailyTasks();
        localStorage.setItem('workflow_lastDailyReset', today);
      }
    };

    checkForAutoReset();
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="section-title mb-0">Workflow Engine</h1>
        <div className="flex gap-4">
          <div className="text-right">
            <div className="text-sm text-brown/60">Daily Progress</div>
            <div className="text-2xl font-bold text-brown">{Math.round(dailyProgress)}%</div>
          </div>
          <div className="text-right">
            <div className="text-sm text-brown/60">Weekly Progress</div>
            <div className="text-2xl font-bold text-gold">{Math.round(weeklyProgress)}%</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Enhanced Daily Checklist */}
        <div className="glass-card rounded-3xl p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-brown flex items-center">
              <SafeIcon name="Clock" className="mr-3 text-gold" />
              Daily Workflow
            </h2>
            <motion.button
              onClick={resetDailyTasks}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-beige text-brown rounded-xl font-medium hover:bg-beige/80 transition-colors text-sm"
            >
              Reset Daily
            </motion.button>
          </div>

          {/* Daily Progress Bar */}
          <div className="mb-6">
            <div className="h-3 bg-beige/50 rounded-full overflow-hidden">
              <motion.div
                className="h-full gradient-gold rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${dailyProgress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>

          <div className="space-y-4 relative">
            {/* Vertical Timeline Line */}
            <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-gradient-to-b from-gold/50 to-apache/50"></div>
            
            {Object.entries(dailyTasks).map(([taskId, task], index) => (
              <motion.div
                key={taskId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative flex items-center z-10"
              >
                <motion.button
                  onClick={() => toggleDailyTask(taskId)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`w-14 h-14 rounded-full shadow-lg transition-all duration-300 ${
                    task.completed
                      ? 'gradient-gold text-white shadow-apple-hover'
                      : 'bg-white border-2 border-gold/30 text-brown/40'
                  }`}
                >
                  <SafeIcon name={task.completed ? 'Check' : task.icon} className="w-6 h-6" />
                </motion.button>
                
                <motion.div
                  onClick={() => toggleDailyTask(taskId)}
                  whileHover={{ x: 4 }}
                  className="ml-4 flex-1 p-4 glass-card rounded-2xl cursor-pointer group"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-xs font-bold gradient-gold bg-clip-text text-transparent mb-1">
                        {task.time}
                      </div>
                      <div className={`font-bold text-lg transition-all ${
                        task.completed ? 'text-brown/40 line-through' : 'text-brown'
                      }`}>
                        {task.task}
                      </div>
                    </div>
                    {task.completed && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-8 h-8 bg-apache text-white rounded-full flex items-center justify-center"
                      >
                        <SafeIcon name="Check" className="w-4 h-4" />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Enhanced Weekly Checklist */}
        <div className="glass-card rounded-3xl p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-brown flex items-center">
              <SafeIcon name="Calendar" className="mr-3 text-gold" />
              Weekly Rhythm
            </h2>
            <motion.button
              onClick={resetWeeklyTasks}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-beige text-brown rounded-xl font-medium hover:bg-beige/80 transition-colors text-sm"
            >
              Reset Weekly
            </motion.button>
          </div>

          {/* Weekly Progress Bar */}
          <div className="mb-6">
            <div className="h-3 bg-beige/50 rounded-full overflow-hidden">
              <motion.div
                className="h-full gradient-apache rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${weeklyProgress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {Object.entries(weeklyTasks).map(([day, dayData], index) => {
              const dayProgress = (dayData.tasks.filter(t => t.completed).length / dayData.tasks.length) * 100;
              
              return (
                <motion.div
                  key={day}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card rounded-2xl p-5 border border-white/30 hover:border-gold/30 transition-all duration-300"
                >
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg text-brown">{day}</span>
                      <span className="text-xs font-bold px-3 py-1 gradient-gold text-white rounded-full uppercase">
                        {dayData.title}
                      </span>
                    </div>
                    <div className="text-sm font-bold text-gold">{Math.round(dayProgress)}%</div>
                  </div>

                  {/* Day Progress Bar */}
                  <div className="h-2 bg-beige/30 rounded-full overflow-hidden mb-3">
                    <motion.div
                      className="h-full gradient-gold rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${dayProgress}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>

                  <div className="space-y-2">
                    {dayData.tasks.map((task, taskIndex) => (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + taskIndex * 0.05 }}
                        whileHover={{ x: 4 }}
                        onClick={() => toggleWeeklyTask(day, task.id)}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gold/10 transition-colors cursor-pointer"
                      >
                        <motion.button
                          whileHover={{ scale: 1.2 }}
                          whileTap={{ scale: 0.8 }}
                          className={`w-5 h-5 rounded-full transition-all ${
                            task.completed
                              ? 'bg-apache text-white'
                              : 'bg-beige border-2 border-gold/30'
                          }`}
                        >
                          {task.completed && <SafeIcon name="Check" className="w-3 h-3" />}
                        </motion.button>
                        <span className={`text-sm transition-all ${
                          task.completed ? 'text-brown/40 line-through' : 'text-brown'
                        }`}>
                          {task.text}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              );
            })}

            {/* Weekend Card */}
            <motion.div
              className="glass-card rounded-2xl p-5 opacity-75"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 0.75, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex justify-between items-center mb-3">
                <span className="font-bold text-lg text-brown/60">Weekend</span>
                <span className="text-xs font-bold px-3 py-1 bg-brown/10 text-brown/60 rounded-full uppercase">
                  Review
                </span>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-brown/60 flex items-center">
                  <span className="w-2 h-2 bg-brown/30 rounded-full mr-3"></span>
                  Light Engagement
                </div>
                <div className="text-sm text-brown/60 flex items-center">
                  <span className="w-2 h-2 bg-brown/30 rounded-full mr-3"></span>
                  Weekly Strategy Review
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WorkflowEngine;